import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Bold,
  Edit2,
  ExternalLink,
  Highlighter,
  Image as ImageIcon,
  Italic,
  Link as LinkIcon,
  Minus,
  Newspaper,
  Plus,
  Search,
  Trash2,
  Type,
  X,
} from "lucide-react";
import { CONTENT } from "@/constants/testIds";

import { API } from "../lib/api";
const ax = () => axios.create({ withCredentials: true });
const READ_STORAGE_KEY = "schumann_read_bulletins_v1";

function formatDate(dt) {
  if (!dt) return "";
  try {
    return new Date(String(dt).replace(" ", "T")).toLocaleDateString("pt-BR");
  } catch {
    return dt;
  }
}

function bulletinTitle(date = new Date()) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = String(date.getFullYear());
  return `Boletim ${dd}/${mm}/${yyyy}`;
}

function stripHtml(html = "") {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

function hasMeaningfulContent(html = "") {
  return Boolean(stripHtml(html).trim() || /<img[\s>]/i.test(html));
}

function firstLink(item) {
  if (item?.button_link) return item.button_link;
  if (Array.isArray(item?.tags)) return item.tags.find(Boolean) || "";
  if (typeof item?.tags === "string") return item.tags;
  return "";
}

function buttonLabel(item) {
  return item?.button_label || item?.category || "";
}

function isEdited(item) {
  if (!item?.created_at || !item?.updated_at) return false;
  const created = new Date(String(item.created_at).replace(" ", "T")).getTime();
  const updated = new Date(String(item.updated_at).replace(" ", "T")).getTime();
  if (Number.isNaN(created) || Number.isNaN(updated)) return false;
  return updated - created > 5000;
}

function getReadMap() {
  try {
    return JSON.parse(localStorage.getItem(READ_STORAGE_KEY) || "{}");
  } catch {
    return {};
  }
}

function setReadItem(item) {
  if (!item?.item_id) return;
  const map = getReadMap();
  map[item.item_id] = item.updated_at || item.created_at || new Date().toISOString();
  localStorage.setItem(READ_STORAGE_KEY, JSON.stringify(map));
}

function wasReadAfterEdit(item) {
  if (!item?.item_id) return false;
  const map = getReadMap();
  return map[item.item_id] === (item.updated_at || item.created_at);
}

function EditorToolbar({ onCommand, onImage }) {
  return (
    <div className="flex flex-wrap items-center gap-1 rounded-t-2xl border border-slate-200 bg-slate-50 p-2">
      <button type="button" className="editor-tool" title="Negrito" onClick={() => onCommand("bold")}>
        <Bold className="h-4 w-4" />
      </button>
      <button type="button" className="editor-tool" title="Itálico" onClick={() => onCommand("italic")}>
        <Italic className="h-4 w-4" />
      </button>
      <button type="button" className="editor-tool" title="Grifar" onClick={() => onCommand("backColor", "#fff1a8")}>
        <Highlighter className="h-4 w-4" />
      </button>
      <span className="mx-1 h-6 w-px bg-slate-200" />
      <button type="button" className="editor-tool" title="Fonte menor" onClick={() => onCommand("fontSize", "2")}>
        <Minus className="h-4 w-4" />
      </button>
      <button type="button" className="editor-tool" title="Fonte normal" onClick={() => onCommand("fontSize", "3")}>
        <Type className="h-4 w-4" />
      </button>
      <button type="button" className="editor-tool" title="Fonte maior" onClick={() => onCommand("fontSize", "5")}>
        <Plus className="h-4 w-4" />
      </button>
      <span className="mx-1 h-6 w-px bg-slate-200" />
      <label className="editor-tool cursor-pointer" title="Inserir imagem">
        <ImageIcon className="h-4 w-4" />
        <input type="file" accept="image/jpeg,image/png,image/gif" className="hidden" onChange={onImage} />
      </label>
    </div>
  );
}

export default function BulletinPage() {
  const { hasAnyRole } = useAuth();
  const canEdit = hasAnyRole(["autor_area", "administrador_total", "admin"]);
  const editorRef = useRef(null);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: bulletinTitle(), content: "", category: "", tags: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorHasContent, setEditorHasContent] = useState(false);

  const load = () => {
    ax().get(`${API}/bulletins`)
      .then(response => setItems(Array.isArray(response.data) ? response.data : []))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (showForm && editorRef.current) {
      editorRef.current.innerHTML = form.content || "";
      setEditorHasContent(hasMeaningfulContent(form.content || ""));
    }
  }, [showForm, editing?.item_id, form.content]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return items.filter(item => {
      const text = `${item.title || ""} ${stripHtml(item.content || "")} ${buttonLabel(item)} ${firstLink(item)}`.toLowerCase();
      return text.includes(term);
    });
  }, [items, search]);

  const openCreate = () => {
    setEditing(null);
    setForm({ title: bulletinTitle(), content: "", category: "", tags: "" });
    setEditorHasContent(false);
    setShowForm(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || bulletinTitle(),
      content: item.content || "",
      category: buttonLabel(item),
      tags: firstLink(item),
    });
    setEditorHasContent(hasMeaningfulContent(item.content || ""));
    setShowForm(true);
  };

  const updateEditorContent = () => {
    setEditorHasContent(hasMeaningfulContent(editorRef.current?.innerHTML || ""));
  };

  const execEditorCommand = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    updateEditorContent();
  };

  const insertImage = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
      window.alert("Use apenas imagens JPEG, PNG ou GIF.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      editorRef.current?.focus();
      document.execCommand("insertHTML", false, `<img src="${reader.result}" alt="" style="max-width:100%;border-radius:16px;margin:12px 0;" />`);
      updateEditorContent();
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    const content = editorRef.current?.innerHTML || form.content || "";
    const payload = {
      title: editing ? form.title : bulletinTitle(),
      content,
      category: form.category,
      tags: form.tags ? [form.tags.trim()] : [],
    };

    setSaving(true);
    try {
      if (editing) {
        await ax().put(`${API}/bulletins/${editing.item_id}`, payload);
      } else {
        await ax().post(`${API}/bulletins`, payload);
      }
      setShowForm(false);
      load();
    } catch (error) {
      console.error(error);
      window.alert("Não foi possível salvar o boletim. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este boletim?")) return;
    await ax().delete(`${API}/bulletins/${id}`);
    load();
    if (selected?.item_id === id) setSelected(null);
  };

  const openItem = (item) => {
    setSelected(selected?.item_id === item.item_id ? null : item);
    setReadItem(item);
    setItems(current => [...current]);
  };

  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-5 flex items-center justify-between animate-fade-in">
        <div className="flex items-center gap-2">
          <Newspaper className="h-5 w-5 text-[#2346ff]" />
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Boletim</h1>
            <p className="text-sm text-slate-500">{items.length} publicações</p>
          </div>
        </div>
        {canEdit && (
          <Button data-testid={CONTENT.createBtn} onClick={openCreate} style={{ backgroundColor: "#2346ff" }} className="gap-2 text-white">
            <Plus className="h-4 w-4" /> Novo Boletim
          </Button>
        )}
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input data-testid={CONTENT.searchInput} placeholder="Buscar boletins..." value={search} onChange={event => setSearch(event.target.value)} className="pl-9" />
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, index) => <div key={index} className="h-24 animate-pulse rounded-xl bg-slate-100" />)}
        </div>
      ) : (
        <div className="space-y-3" data-testid={CONTENT.contentList}>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-slate-400">
              <Newspaper className="mx-auto mb-3 h-12 w-12 opacity-30" />
              <p>Nenhum boletim encontrado</p>
            </div>
          )}

          {filtered.map(item => {
            const link = firstLink(item);
            const label = buttonLabel(item);
            const hasUnreadEdit = isEdited(item) && !wasReadAfterEdit(item);
            const preview = stripHtml(item.content || "");

            return (
              <div
                key={item.item_id}
                data-testid={CONTENT.contentItem}
                className="bento-card group cursor-pointer p-4"
                onClick={() => openItem(item)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {hasUnreadEdit && (
                        <span
                          className="h-2.5 w-2.5 rounded-full bg-orange-400 shadow-[0_0_0_4px_rgba(251,146,60,0.16)]"
                          title="Boletim atualizado desde sua última leitura"
                        />
                      )}
                      <h3 className="font-semibold text-slate-800 transition-colors group-hover:text-[#2346ff]">{item.title}</h3>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-slate-500">{preview || "Clique para ler o boletim."}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <span className="text-xs text-slate-400">{item.created_by_name}</span>
                      <span className="text-xs text-slate-300">•</span>
                      <span className="text-xs text-slate-400">{formatDate(item.created_at)}</span>
                      {isEdited(item) && <span className="text-xs font-semibold text-orange-500">Atualizado</span>}
                    </div>
                  </div>

                  <div className="flex shrink-0 items-center gap-2">
                    {canEdit && (
                      <>
                        <Button data-testid={CONTENT.editBtn} variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100" onClick={event => { event.stopPropagation(); openEdit(item); }}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button data-testid={CONTENT.deleteBtn} variant="ghost" size="icon" className="h-8 w-8 text-red-500 opacity-0 group-hover:opacity-100" onClick={event => { event.stopPropagation(); remove(item.item_id); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {selected?.item_id === item.item_id && (
                  <div data-testid={CONTENT.contentDetail} className="mt-4 border-t border-slate-100 pt-4">
                    <div className="prose prose-sm max-w-none text-slate-700 [&_img]:max-w-full [&_img]:rounded-2xl [&_img]:border [&_img]:border-slate-100 [&_img]:shadow-sm" dangerouslySetInnerHTML={{ __html: item.content || "" }} />
                    {link && label && (
                      <div className="mt-5 flex flex-wrap gap-2">
                        <a
                          href={link}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#2346ff] px-4 text-sm font-bold text-white transition hover:bg-[#1d3ace]"
                          onClick={event => event.stopPropagation()}
                        >
                          <LinkIcon className="h-4 w-4" />
                          {label}
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Editar Boletim" : "Novo Boletim"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="rounded-2xl border border-[#dbe2ff] bg-[#f7f8ff] p-4">
              <Label>Título automático</Label>
              <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-white px-4 py-3 text-sm font-bold text-slate-900">
                <span>{editing ? form.title : bulletinTitle()}</span>
                {editing && <span className="rounded-full bg-orange-100 px-2 py-1 text-xs text-orange-600">Edição gera notificação</span>}
              </div>
              <p className="mt-2 text-xs text-slate-500">Novos boletins sempre usam a data do dia no padrão Boletim dd/mm/aaaa.</p>
            </div>

            <div>
              <Label>Conteúdo do boletim *</Label>
              <EditorToolbar onCommand={execEditorCommand} onImage={insertImage} />
              <div
                ref={editorRef}
                contentEditable
                onInput={updateEditorContent}
                className="min-h-[220px] rounded-b-2xl border-x border-b border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700 outline-none focus:border-[#2346ff] [&_img]:max-w-full [&_img]:rounded-2xl"
                data-placeholder="Digite o conteúdo do boletim..."
                suppressContentEditableWarning
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label>Nome do botão</Label>
                <Input value={form.category} onChange={event => setForm(current => ({ ...current, category: event.target.value }))} placeholder="Ex.: Abrir regulamento" />
              </div>
              <div>
                <Label>Link</Label>
                <Input value={form.tags} onChange={event => setForm(current => ({ ...current, tags: event.target.value }))} placeholder="https://drive.google.com/..." />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button data-testid={CONTENT.cancelBtn} variant="outline" onClick={() => setShowForm(false)}>
              <X className="mr-1 h-4 w-4" />
              Cancelar
            </Button>
            <Button data-testid={CONTENT.saveBtn} onClick={save} disabled={saving || !editorHasContent} style={{ backgroundColor: "#2346ff" }} className="text-white">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <style>{`
        .editor-tool {
          display: inline-grid;
          min-height: 34px;
          min-width: 34px;
          place-items: center;
          border-radius: 10px;
          color: #2346ff;
          transition: background-color 0.2s ease, color 0.2s ease;
        }
        .editor-tool:hover {
          background: #eef1ff;
        }
        [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
        }
      `}</style>
    </div>
  );
}
